/**
 * 网络请求加密
 */

import { PROJECT_CODE, PUBLIC_KEY } from '@/config/config'
import { getUnixTime } from 'date-fns'
import { Base64 } from 'js-base64'
import { formatObject } from '@/utils/util'
import { JSEncrypt } from 'jsencrypt-ext'
import { cloneDeep } from 'lodash-es'
import { sha256 } from 'js-sha256'
import CryptoJS from 'crypto-js'
import { AxiosRequestConfig } from 'axios'
import { ContentTypeEnum } from '@/enum/httpEnum'
import { currentTrueTime } from '@/utils/global-time'

interface Signature {
  timestamp: number
  method: string
  path: string
  query: string
  bodySign: string
}

enum Method {
  GET = 'GET',
  POST = 'POST'
}

/**自定义key */
const key = PROJECT_CODE

const encryptor = new JSEncrypt({
  default_key_size: '512'
})

/**设置公钥 */
encryptor.setPublicKey(PUBLIC_KEY)

/**
 * 对网络请求进行加密处理
 * 步骤流程：
 * 需要通过该请求获取一个签名放在X-Content-Security ，签名由三部分组成，{secret , key , signature }
 * 1. secret也是由三部分组成{type, key, time,} , type是固定0 ， key是自定义key的base64数据格式，time就是当前时间戳； 通过该对象格式化成type=;key=;time= 这个字符串类型再进行rsa加密。
 * 2. key 是自定义值 也用在设置secret里面
 * 3. signature是通过五个数据去获取的，{timestamp , method , path , query , bodySign}, timestamp是当前时间戳，method是请求类型(get / post)
 * path是请求路径，query是路由query，bodySign是请求体；首先将请求体bodySign转换成json字符串，再进行rsa加密（rsa加密后的字符串还需要放置在请求的请求体里面），再进行sha256加密；
 * 之后将该对象的value通过\n(换行符)进行分隔得到字符串，先对该字符串用key为盐进行hmac加密，再将加密后得到的二进制数组通过特定的方法转换成字符串，该字符串就是signature
 * 4. 得到前面三个数据之后再转换成 key= ; secret= ;signature= 这个字符串。就完成
 */

/**对请求进行加密且返回请求config */
export function getEncryptConfig(config: AxiosRequestConfig) {
  const _config = cloneDeep(config)

  /**当前时间戳 */
  const timestamp = getUnixTime(currentTrueTime())

  /**获取secret */
  const secret = getSecret(timestamp)

  /**对请求体进行rsa加密 */
  const _data = cloneDeep(_config.data)
  const method = _config.method === 'post' ? Method.POST : Method.GET
  let query = ''
  let rsaParmas = ''
  if (method === Method.GET) {
    /**需要获取query */
    // @ts-ignore
    query = formatObject(_config.params, '&')
  } else {
    const params = JSON.stringify(_data)
    rsaParmas = encrypt(params)
  }

  /**先获取五个部分 */
  const signatureObj: Signature = {
    /**时间戳 */
    timestamp,
    /**请求方式 大写 */
    method: _config.method!.toLocaleUpperCase(),
    /**请求路径 */
    path: _config.url || '',
    /**query */
    query,
    /**请求体的rsa加密值 */
    bodySign: rsaParmas
  }

  /**获取signature */
  const signature = getSignature(signatureObj)

  /**获取最后放到请求头 X-Content-Security 的字符串*/
  const finalStr = formatObject({
    key,
    secret,
    signature
  })

  /**将加密后的数据放在请求头的 X-Content-Security 属性上 */
  _config.headers && (_config.headers['X-Content-Security'] = finalStr)
  /**将请求体设置成加密后的请求体 */
  _config.data = rsaParmas

  _config.headers!['content-type'] = ContentTypeEnum.JSON
  return _config
}

/**获取secert */
function getSecret(time: number) {
  const obj: any = {
    type: 0,
    key: Base64.encode(PROJECT_CODE),
    time
  }

  /**获取对应格式的字符串 */
  const str = formatObject(obj)

  /**rsa加密 */
  const rsaSecret = encrypt(str)

  return rsaSecret
}

/**获取signature */
function getSignature(data: Signature) {
  const _data = cloneDeep(data)
  /**对rsa加密后的请求体 进行 sha256加密 */
  _data.bodySign = sha256(data.bodySign)
  /**获取字符串 */
  const body = formatObject(_data, '\n', false)

  /**先用hmac key为PROJECTCODE 进行加密 */
  const hmacBody = CryptoJS.HmacSHA256(body, PROJECT_CODE)

  /**将byte[]数组转成字符串 需要特定方法 */
  const baseBody = CryptoJS.enc.Base64.stringify(hmacBody)

  return baseBody
}

/**rsa加密 */
function encrypt(str: string) {
  return encryptor.encrypt(emojiEncode(str)) || ''
}

/**将emoji编码。否则在加密处理后后端无法识别 */
function emojiEncode(e: any) {
  if (!e) return e
  let n = /[\ud800-\udbff][\udc00-\udfff]/g
  // 检测utf16字符正则
  return (e = e.replace(n, function (e: any) {
    let n: any, r: any
    return 2 === e.length
      ? ((n = e.charCodeAt(0)), (r = e.charCodeAt(1)), '&#' + (1024 * (n - 55296) + 65536 + r - 56320) + ';')
      : e
  }))
}

/**将emoji解码 */
export function emojiDecode(e: any): string {
  if (!e) return e
  let n = /\&#.*?;/g //获取数据库种16进制的数据
  //然后进行解码操作
  return e.replace(n, function (e: any) {
    let n, r, t
    return 9 == e.length
      ? ((t = parseInt(e.match(/[0-9]+/g))),
        (n = Math.floor((t - 65536) / 1024) + 55296),
        (r = ((t - 65536) % 1024) + 56320),
        unescape('%u' + n.toString(16) + '%u' + r.toString(16)))
      : e
  })
}
